import { useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import { actions, listRequirementCampaigns, listVendors } from "@/lib/api";
import { displayDate } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useData, matches, reportActionError, type ViewProps, type DialogProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";

export function Campaigns({ organisation, refresh, query, can, changed }: ViewProps) {
  const [{ data: rows, error, loading }, { data: vendors }] = [
    useData(
      () => listRequirementCampaigns(organisation.id),
      [organisation.id, refresh],
    ),
    useData(() => listVendors(organisation.id), [organisation.id, refresh]),
  ];
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState("");
  if (loading) return <Loading />;
  if (error || !rows) return <ErrorState message={error} />;
  const filtered = rows.filter((row) =>
    matches(query, row.title, row.subject, row.status),
  );
  async function publish(id: string) {
    setBusy(id);
    try {
      await actions.publishRequirementCampaign(organisation.id, id);
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }
  return (
    <>
      <PageHeader
        eyebrow="SUPPLIER OUTREACH"
        title="Requirement bulletins"
        description="Package requirements once and deliver them to selected supplier portals."
      >
        {can("procure") && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" />
            New bulletin
          </Button>
        )}
      </PageHeader>
      {filtered.length ? (
        <Table
          heads={[
            "Bulletin",
            "Requirements",
            "Recipients",
            "Deadline",
            "Status",
            "Action",
          ]}
        >
          {filtered.map((row) => (
            <tr key={row.id}>
              <td>
                <strong>{row.title}</strong>
                <small className="block text-slate-500">{row.subject}</small>
              </td>
              <td>{row.requirement_campaign_items?.length || 0}</td>
              <td>{row.requirement_campaign_recipients?.length || 0}</td>
              <td>{displayDate(row.response_deadline, true)}</td>
              <td>
                <Badge value={row.status} />
              </td>
              <td>
                {can("procure") && row.status === "draft" && (
                  <Button
                    size="sm"
                    disabled={busy === row.id}
                    onClick={() => publish(row.id)}
                  >
                    Publish to suppliers
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      ) : (
        <Empty
          title="No requirement bulletins"
          description="Create a concise requirement newsletter and choose the companies that should receive it."
        />
      )}
      <CampaignDialog
        open={creating}
        onOpenChange={setCreating}
        organisationId={organisation.id}
        vendors={vendors || []}
        onDone={changed}
      />
    </>
  );
}

function CampaignDialog({
  open,
  onOpenChange,
  organisationId,
  vendors,
  onDone,
}: DialogProps & { vendors: Awaited<ReturnType<typeof listVendors>> }) {
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.createRequirementCampaign(organisationId, {
        p_title: form.get("title"),
        p_subject: form.get("subject"),
        p_introduction: form.get("introduction"),
        p_response_deadline: new Date(
          String(form.get("deadline")),
        ).toISOString(),
        p_currency: String(form.get("currency")).toUpperCase(),
        p_items: [
          {
            description: form.get("item"),
            specification: form.get("specification"),
            quantity: Number(form.get("quantity")),
            unit: form.get("unit"),
            delivery_location: form.get("delivery"),
          },
        ],
        p_vendor_ids: selected,
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="SUPPLIER OUTREACH"
          title="Create requirement bulletin"
          description="This is delivered inside each selected supplier portal; no external mailing list is created."
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Campaign name
              <Input name="title" required />
            </Label>
            <Label>
              Supplier-facing subject
              <Input name="subject" required />
            </Label>
            <Label>
              Response deadline
              <Input name="deadline" type="datetime-local" required />
            </Label>
            <Label>
              Reference currency
              <Input
                name="currency"
                defaultValue="INR"
                pattern="[A-Za-z]{3}"
                maxLength={3}
                required
              />
            </Label>
            <Label>
              Requirement
              <Input name="item" required />
            </Label>
            <Label>
              Quantity
              <Input
                name="quantity"
                type="number"
                min="0.01"
                step="0.01"
                required
              />
            </Label>
            <Label>
              Unit
              <Input name="unit" required />
            </Label>
            <Label>
              Delivery location
              <Input name="delivery" />
            </Label>
          </div>
          <Label>
            Introduction
            <Textarea name="introduction" required />
          </Label>
          <Label>
            Specification
            <Textarea name="specification" required />
          </Label>
          <div>
            <strong className="text-xs">Recipient companies</strong>
            <div className="mt-2 max-h-40 overflow-auto rounded-xl border border-slate-200">
              {vendors
                .filter((v) => v.active)
                .map((v) => (
                  <label
                    key={v.id}
                    className="flex items-center gap-3 border-b border-slate-100 p-3 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(v.id)}
                      onChange={(event) =>
                        setSelected((current) =>
                          event.target.checked
                            ? [...current, v.id]
                            : current.filter((id) => id !== v.id),
                        )
                      }
                    />
                    {v.name} · {v.country_code}
                  </label>
                ))}
            </div>
          </div>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy || !selected.length} type="submit">
              Save bulletin
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
